import React from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { CHARACTER_WIDTHS } from '../../types/constants';
import { GreenroomState } from '../../stores/types/types';

interface TitleUser {
	first_name: string;
	last_name: string;
	location?: string;
	title: string;
}

interface Props {
	cardType: number;
	user: TitleUser;
	// specific styling variable for the dropdown in settings tab
	preview: boolean;
	position?: string;
}

export default function TitleCard(props: Props) {
	const {
		cardType,
		user,
		preview,
	} = props;

	const {
		titlesUseLocation,
		titlesUseName,
		titlesUseTitle,
		titlesFontColor1,
		titlesFontColor2,
		titlesBackgroundColor1,
		titlesBackgroundColor2,
		titlesBackgroundColor3,
	} = useSelector((state: GreenroomState) => state.ShotsReducer.canvasStyle);

	const firstName = user.first_name ?? '';
	const lastName = user.last_name ?? '';
	const location = user.location ?? '';
	const title = user.title ?? '';
	const fullName = `${firstName} ${lastName}`;

	function getStringLength(string: string, divideByNumber: number, minPercent: number) {
		// divideByNumber is different based on the svg component.
		// Bigger number = smaller component overall.

		let wordWidth = 0;

		const letterArray = string.split('');

		const len = letterArray.length;
		let i = 0;
		for (i; i < len; i += 1) {
			// @ts-ignore
			let charWidth = CHARACTER_WIDTHS[letterArray[i]];

			// if not alphabet char, generic 45 width
			charWidth = charWidth || 65;

			wordWidth += charWidth;
		}

		let percentWidth = Math.floor((wordWidth / divideByNumber) * 100);

		// min width
		percentWidth = percentWidth < minPercent ? minPercent : percentWidth;

		if (percentWidth > 100) {
			percentWidth = 100;
		}

		return percentWidth;
	}

	// These "cardStringsX" are very verbose, but when dealing with 3 bools it gets this way-
	// I tried to find a less annoying way of doing it, but this is the clearest for debugging later
	// see https://stackoverflow.com/questions/44921181/better-way-of-finding-combination-for-3-boolean-variables

	const hasLoc = (titlesUseLocation && !!location);
	const hasName = (titlesUseName && !!fullName);
	const hasTitle = (titlesUseTitle && !!title);

	function cardStrings1() {
		switch (true) {
			case hasLoc && hasTitle && hasName:
				return { text1: location, text2: fullName, text3: title };
			case !hasLoc && hasTitle && hasName:
				return { text1: '', text2: fullName, text3: title };
			case hasLoc && !hasTitle && hasName:
				return { text1: '', text2: fullName, text3: location };
			case hasLoc && hasTitle && !hasName:
				return { text1: '', text2: title, text3: location };
			case hasLoc && !hasTitle && !hasName:
				return { text1: '', text2: location, text3: '' };
			case !hasLoc && hasTitle && !hasName:
				return { text1: '', text2: title, text3: '' };
			case !hasLoc && !hasTitle && hasName:
				return { text1: '', text2: fullName, text3: '' };
			default:
				return { text1: '', text2: '', text3: '' };
		}
	}

	function cardStrings2() {
		switch (true) {
			case hasLoc && hasTitle && hasName:
				return { text1: location, text2: fullName, text3: title };
			case !hasLoc && hasTitle && hasName:
				return { text1: '', text2: fullName, text3: title };
			case hasLoc && !hasTitle && hasName:
				return { text1: '', text2: fullName, text3: location };
			case hasLoc && hasTitle && !hasName:
				return { text1: '', text2: title, text3: location };
			case hasLoc && !hasTitle && !hasName:
				return { text1: '', text2: location, text3: '' };
			case !hasLoc && hasTitle && !hasName:
				return { text1: '', text2: title, text3: '' };
			case !hasLoc && !hasTitle && hasName:
				return { text1: '', text2: fullName, text3: '' };
			default:
				return { text1: '', text2: '', text3: '' };
		}
	}

	// these go from top -> bottom left -> right on the actual card
	// (1 is highest and most left, then 2, then 3)
	function cardStrings4() {
		switch (true) {
			case hasLoc && hasTitle && hasName:
				return { text1: location, text2: fullName, text3: title };
			case !hasLoc && hasTitle && hasName:
				return { text1: '', text2: fullName, text3: title };
			case hasLoc && !hasTitle && hasName:
				return { text1: '', text2: fullName, text3: location };
			case hasLoc && hasTitle && !hasName:
				return { text1: '', text2: title, text3: location };
			case hasLoc && !hasTitle && !hasName:
				return { text1: '', text2: location, text3: '' };
			case !hasLoc && hasTitle && !hasName:
				return { text1: '', text2: title, text3: '' };
			case !hasLoc && !hasTitle && hasName:
				return { text1: '', text2: fullName, text3: '' };
			default:
				return { text1: '', text2: '', text3: '' };
		}
	}

	function cardStrings5() {
		switch (true) {
			case hasLoc && hasTitle && hasName:
				return { text1: title, text2: fullName, text3: location };
			case !hasLoc && hasTitle && hasName:
				return { text1: '', text2: fullName, text3: title };
			case hasLoc && !hasTitle && hasName:
				return { text1: '', text2: fullName, text3: location };
			case hasLoc && hasTitle && !hasName:
				return { text1: '', text2: title, text3: location };
			case hasLoc && !hasTitle && !hasName:
				return { text1: '', text2: location, text3: '' };
			case !hasLoc && hasTitle && !hasName:
				return { text1: '', text2: title, text3: '' };
			case !hasLoc && !hasTitle && hasName:
				return { text1: '', text2: fullName, text3: '' };
			default:
				return { text1: '', text2: '', text3: '' };
		}
	}

	function cardStrings6() {
		switch (true) {
			case hasLoc && hasTitle && hasName:
				return { text1: fullName, text2: title };
			case !hasLoc && hasTitle && hasName:
				return { text1: fullName, text2: title };
			case hasLoc && !hasTitle && hasName:
				return { text1: fullName, text2: location };
			case hasLoc && hasTitle && !hasName:
				return { text1: title, text2: location };
			case hasLoc && !hasTitle && !hasName:
				return { text1: location, text2: '' };
			case !hasLoc && hasTitle && !hasName:
				return { text1: title, text2: '' };
			case !hasLoc && !hasTitle && hasName:
				return { text1: fullName, text2: '' };
			default:
				return { text1: '', text2: '' };
		}
	}

	function cardStrings7() {
		switch (true) {
			case hasLoc && hasTitle && hasName:
				return { text1: title, text2: fullName };
			case !hasLoc && hasTitle && hasName:
				return { text1: title, text2: fullName };
			case hasLoc && !hasTitle && hasName:
				return { text1: location, text2: fullName };
			case hasLoc && hasTitle && !hasName:
				return { text1: location, text2: title };
			case hasLoc && !hasTitle && !hasName:
				return { text1: '', text2: location };
			case !hasLoc && hasTitle && !hasName:
				return { text1: '', text2: title };
			case !hasLoc && !hasTitle && hasName:
				return { text1: '', text2: fullName };
			default:
				return { text1: '', text2: '' };
		}
	}

	let width = '';
	let margin = '';
	let position = '';
	// 1 isnt full width, if it were flexbox positioning wouldnt work
	if (!preview) {
		width = 'max-width';
	}
	// some titles are flush to left side of video
	if (cardType === 1 || cardType === 2 || cardType === 6 || cardType === 7) {
		margin = 'no-left-margin';
	}
	// titles inside the drop down in settings have to have static positioning
	if (preview) {
		position = 'static-preview';
	}

	function titleSvg() {
		switch (cardType) {
			case 1: {
				const { text1, text2, text3 } = cardStrings1();
				// If only 1 of the 3 title text strings is available,
				// center that 1 text (ex: user has a name, but not a location or title)
				const centerText = (!text1 && !text2) || (!text1 && !text3) || (!text2 && !text3);

				return (
					<svg viewBox="0 0 1000 120" width="100%">
						{text1 && (
							<svg
								height="32"
								x="-1"
								y="86.5"
								width={`${getStringLength(text1, 5000, 42)}%`}
							>
								<rect
									height="100%"
									width="100%"
									fill={titlesBackgroundColor3}
								/>
								<text
									x="27"
									y="21"
									fontSize="16px"
									fontWeight="700"
									fill={titlesFontColor2}
								>
									{text1}
								</text>
							</svg>
						)}

						<svg
							width={`${Math.max(
								getStringLength(text2, 2500, 51.6),
								getStringLength(text3, 4200, 51.6),
							)}%`}
							height="87"
							viewBox="0 0 8 87"
							x="-2.5"
							y={text1 ? '0' : '32'}
							preserveAspectRatio="xMaxYMid"
						>
							<rect width="9" height="87" fill={titlesBackgroundColor2} />
						</svg>
						<svg
							height="87"
							width={`${Math.max(
								getStringLength(text2, 2500, 51.6),
								getStringLength(text3, 4200, 51.6),
							)}%`}
							x="-1"
							y={text1 ? '0' : '32'}
						>
							<rect
								height="100%"
								width="calc(100% - 9px)"
								fill={titlesBackgroundColor1}
							/>
							{/* This svg is for cutting off long text at the end */}
							<svg width={`${99}%`} x=".5">
								<text
									x="27"
									y={centerText ? 55 : 44}
									fontSize="32px"
									fill={titlesFontColor1}
									fontWeight="bold"
								>
									{text2}
								</text>
								<text
									x="27"
									y="70"
									fontSize="20px"
									fill={titlesFontColor1}
									fontWeight="500"
								>
									{text3}
								</text>
							</svg>
						</svg>
					</svg>
				);
			}

			case 2: {
				const { text1, text2, text3 } = cardStrings2();
				// If only 1 of the 3 title text strings is available,
				// center that 1 text (ex: user has a name, but not a location or title)
				const centerText = (!text1 && !text2) || (!text1 && !text3) || (!text2 && !text3);

				return (
					<svg viewBox="0 0 1000 120" width="100%">
						{text1 && (
							<svg
								height="32"
								x="-1"
								y="86.5"
								width={`${getStringLength(text1, 5000, 42)}%`}
							>
								<rect
									height="100%"
									width="100%"
									fill={titlesBackgroundColor3}
								/>
								<text
									x="27"
									y="21"
									fontSize="16px"
									fontWeight="700"
									fill={titlesFontColor2}
								>
									{text1}
								</text>
							</svg>
						)}

						<svg
							height="87"
							viewBox="0 0 42 87"
							width={`${Math.max(
								getStringLength(text2, 2400, 51.6),
								getStringLength(text3, 4000, 51.6),
							)}%`}
							x="-1"
							y={text1 ? '0' : '32'}
							preserveAspectRatio="xMaxYMid"
						>
							{/* Added some extra rects to cover up 1px line that was showing up at certain widths,
							then realized my external monitors were showing extra lines that my macbook wasn't.
							Left them in anyways. Lots of fiddling with pixels to get things perfect.
							Copying svg from Figma seemed harder than necessary to get things perfect */}
							<rect
								width="10"
								height="87"
								fill={titlesBackgroundColor2}
								transform="translate(-.5, 0)"
							/>
							<rect
								width="3"
								height="10"
								fill={titlesBackgroundColor1}
								transform="translate(-2.5, 75)"
							/>
							<rect
								width="6"
								height="82"
								fill={titlesBackgroundColor1}
								transform="translate(-4.5, 0)"
							/>
							<path d="M9 87V0H42L9 87Z" fill={titlesBackgroundColor2} />
							<path
								d="M0 87V0H33L0 87Z"
								fill={titlesBackgroundColor1}
								transform="translate(0, -.5)"
							/>
						</svg>
						{/* Get highest width between the presenter name and the subtitle (location/title) */}
						<svg
							height="87"
							width={`${Math.max(
								getStringLength(text2, 2400, 51.6),
								getStringLength(text3, 4000, 51.6),
							)}%`}
							x="-1"
							y={text1 ? '0' : '32'}
						>
							<rect
								height="100%"
								width="calc(100% - 42px)"
								fill={titlesBackgroundColor1}
							/>
							{/* These svgs are for cutting off long text at the end */}
							<svg width={`${97}%`}>
								<text
									x="27"
									y={centerText ? 55 : 44}
									fontSize="32px"
									fill={titlesFontColor1}
									fontWeight="bold"
								>
									{text2}
								</text>
							</svg>
							<svg width={`${96}%`}>
								<text
									x="27"
									y="70"
									fontSize="20px"
									fill={titlesFontColor1}
									fontWeight="500"
								>
									{text3}
								</text>
							</svg>
						</svg>
					</svg>
				);
			}

			case 3: {
				const textAnchor = props?.position?.includes('right')
					? 'end'
					: 'start';
				const xWidth = 1045;
				const xLocation = props?.position?.includes('right')
					? xWidth - 8
					: 8;

				let location1 = -51;
				let location2 = 0;
				let location3 = 40;
				let height = 145;

				if (!titlesUseName) {
					height -= 43;
					location2 = -21.5;
					location3 = 20;

					if (!titlesUseTitle) {
						height -= 40;
						location3 = 0;
					}

					if (!titlesUseLocation) {
						height -= 40;
						location2 = 0;
					}
				} else if (!titlesUseTitle) {
					height -= 24;
					location1 = -23;
					location3 = 29;

					if (!titlesUseLocation) {
						height -= 29;
						location1 = 0;
					}
				} else if (!titlesUseLocation) {
					height -= 24;
					location1 = -23;
					location2 = 29;
				}

				return (
					<div className={classNames('card-3')}>
						<svg
							className="user-data"
							preserveAspectRatio="xMinYMid meet"
							viewBox={`0 0 ${xWidth} ${height}`}
							width="100%"
							height="100%"
						>
							{titlesUseName && (
								<svg
									width="100%"
									viewBox={`0 0 ${xWidth} 50`}
									preserveAspectRatio="xMinYMid meet"
									xmlns="http://www.w3.org/2000/svg"
									y={location1}
								>
									<text
										x={xLocation}
										y="50"
										fontSize="50"
										fill="white"
										textAnchor={textAnchor}
									>
										{fullName}
									</text>
								</svg>
							)}
							{titlesUseTitle && (
								<svg
									width="100%"
									viewBox={`0 0 ${xWidth} 40`}
									preserveAspectRatio="xMinYMid meet"
									xmlns="http://www.w3.org/2000/svg"
									y={location2}
								>
									<text
										x={xLocation}
										y="35"
										fontSize="35"
										fill="white"
										textAnchor={textAnchor}
									>
										{title}
									</text>
								</svg>
							)}
							{titlesUseLocation && (
								<svg
									width="100%"
									viewBox={`0 0 ${xWidth} 40`}
									preserveAspectRatio="xMinYMid meet"
									xmlns="http://www.w3.org/2000/svg"
									y={location3}
								>
									<text
										x={xLocation}
										y="35"
										fontSize="35"
										fill="white"
										textAnchor={textAnchor}
									>
										{location}
									</text>
								</svg>
							)}
						</svg>
					</div>
				);
			}

			case 4: {
				const { text1, text2, text3 } = cardStrings4();

				return (
					<svg viewBox="0 0 1000 127" width="100%">
						{text1 && (
							<svg
								height="29"
								width={`${getStringLength(text1, 2700, 20)}%`}
								x="100"
							>
								<rect
									height="100%"
									width="calc(100% - 56px)"
									x="28"
									fill={titlesBackgroundColor3}
								/>
								<text
									x="36"
									y="22"
									fontSize="22px"
									fill={titlesFontColor1}
									fontStyle="italic"
									fontWeight="bold"
								>
									{text1}
								</text>

								<svg
									viewBox="0 0 10 10"
									height="100%"
									preserveAspectRatio="xMaxYMid"
								>
									<path
										d="M10,10H0V0S10,0.5,10,10Z"
										fill={titlesBackgroundColor3}
									/>
								</svg>

								<svg
									viewBox="0 0 10 10"
									height="100%"
									preserveAspectRatio="xMinYMid"
								>
									<path
										d="M0,10h10V0S0,0.5,0,10Z"
										fill={titlesBackgroundColor3}
									/>
								</svg>
							</svg>
						)}

						<svg height="75" width="100%" y={text3 ? '32' : '43'}>
							<rect
								height="100%"
								width="calc(100% - 72px)"
								x="36"
								fill={titlesBackgroundColor1}
							/>
							<svg width={`${98}%`}>
								<text
									x="45"
									y="51"
									fontSize="32px"
									fill={titlesFontColor1}
									fontStyle="italic"
									fontWeight="bold"
								>
									{text2}
								</text>
							</svg>

							<svg
								viewBox="0 0 5 10"
								height="100%"
								preserveAspectRatio="xMinYMid"
							>
								<path d="M5,10a5,5,0,0,1,0-10Z" fill={titlesBackgroundColor1} />
							</svg>

							<svg
								viewBox="0 0 5 10"
								height="100%"
								preserveAspectRatio="xMaxYMid"
							>
								<path
									d="M0,0a5,5,0,0,1,5,5,5,5,0,0,1-5,5Z"
									fill={titlesBackgroundColor1}
								/>
							</svg>
						</svg>

						{text3 && (
							// x attribute is for chrome, style is for firefox
							<svg
								height="30"
								width={`${getStringLength(text3, 3400, 35)}%`}
								y="97"
								x={`calc(100% - ${getStringLength(text3, 3400, 35)}% - 11.1px)`}
								style={{
									transform: `translate(calc(100% - ${getStringLength(
										text3,
										3400,
										35,
									)}% - 5.8px))`,
								}}
							>
								<rect
									height="100%"
									width="calc(100% - 43px)"
									x="14"
									fill={titlesBackgroundColor2}
								/>
								<svg width={`${97}%`}>
									<text
										x="18"
										y="23"
										fontSize="24px"
										fill={titlesFontColor2}
										fontWeight="bold"
										fontStyle="italic"
									>
										{text3}
									</text>
								</svg>

								<svg
									viewBox="0 0 5 10"
									height="100%"
									preserveAspectRatio="xMinYMid"
								>
									<path
										d="M5,10a5,5,0,0,1,0-10Z"
										fill={titlesBackgroundColor2}
									/>
								</svg>

								<svg
									viewBox="0 0 10 10"
									height="100%"
									preserveAspectRatio="xMaxYMid"
								>
									<polygon
										points="10 0 0 0 0 10 10 0"
										fill={titlesBackgroundColor2}
									/>
								</svg>
							</svg>
						)}
					</svg>
				);
			}

			case 5: {
				const { text1, text2, text3 } = cardStrings5();

				return (
					<svg viewBox="0 0 1000 120" width="100%">
						{text1 && (
							<svg
								height="29"
								width={`${getStringLength(text1, 2800, 20)}%`}
								x="200"
							>
								<rect
									height="100%"
									width="calc(100% - 56px)"
									x="28"
									fill={titlesBackgroundColor3}
								/>

								<text
									x="36"
									y="22"
									fontSize="22px"
									fill={titlesFontColor1}
									fontWeight="bold"
									fontStyle="italic"
								>
									{text1}
								</text>

								<svg
									viewBox="0 0 10 10"
									height="100%"
									preserveAspectRatio="xMaxYMid"
								>
									<path
										d="M10,10H0V0S10,0.5,10,10Z"
										fill={titlesBackgroundColor3}
									/>
								</svg>

								<svg
									viewBox="0 0 10 10"
									height="100%"
									preserveAspectRatio="xMinYMid"
								>
									<path
										d="M0,10h10V0S0,0.5,0,10Z"
										fill={titlesBackgroundColor3}
									/>
								</svg>
							</svg>
						)}

						<svg height="50" width="100%" y={text3 ? '34' : '44'}>
							<rect
								height="100%"
								width="calc(100% - 98px)"
								x="49"
								fill={titlesBackgroundColor1}
							/>
							<svg width={`${98}%`}>
								<text
									x="55"
									y="38"
									fontSize="32px"
									fill={titlesFontColor1}
									fontWeight="bold"
									fontStyle="italic"
								>
									{text2}
								</text>
							</svg>

							<svg
								viewBox="0 0 10 10"
								height="100%"
								preserveAspectRatio="xMinYMid"
							>
								<path
									d="M0,10h10V0S0,0.5,0,10Z"
									fill={titlesBackgroundColor1}
								/>
							</svg>

							<svg
								viewBox="0 0 10 10"
								height="100%"
								preserveAspectRatio="xMaxYMid"
							>
								<path
									d="M0,10V0h10S10,10,0,10Z"
									fill={titlesBackgroundColor1}
								/>
							</svg>
						</svg>

						{text3 && (
							// x attribute is for chrome, style is for firefox
							<svg
								height="30"
								width={`${getStringLength(text3, 3400, 40)}%`}
								y="90"
								x={`calc(100% - ${getStringLength(text3, 3400, 40)}% - 42px)`}
								style={{
									transform: `translate(calc(100% - ${getStringLength(
										text3,
										3400,
										35,
									)}% - 42px))`,
								}}
							>
								<rect
									height="100%"
									width="calc(100% - 83px)"
									x="29"
									fill={titlesBackgroundColor2}
								/>
								<svg width={`${95}%`}>
									<text
										x="29"
										y="23"
										fontSize="24px"
										fill={titlesFontColor2}
										fontWeight="bold"
										fontStyle="italic"
									>
										{text3}
									</text>
								</svg>

								<svg
									viewBox="0 0 10 10"
									height="100%"
									preserveAspectRatio="xMinYMid"
								>
									<path
										d="M0,0h10v10S0,10,0.5,0Z"
										fill={titlesBackgroundColor2}
									/>
								</svg>

								<svg
									viewBox="0 0 20 10"
									height="100%"
									preserveAspectRatio="xMaxYMid"
								>
									<path
										d="M0,10V0h20S10,10,0,10Z"
										fill={titlesBackgroundColor2}
									/>
								</svg>
							</svg>
						)}
					</svg>
				);
			}

			case 6: {
				const { text1, text2 } = cardStrings6();

				return (
					<svg viewBox="0 0 1000 93" width="100%">
						<svg height="70" width="40%">
							<rect
								height="100%"
								width="calc(100% - 104px)"
								fill={titlesBackgroundColor3}
							/>
							<svg
								viewBox="0 0 15 10"
								height="100%"
								preserveAspectRatio="xMaxYMid"
							>
								<path
									d="M0,10,0,0H0L4,2.28l10,7.84Z"
									fill={titlesBackgroundColor3}
								/>
							</svg>
						</svg>

						<svg height="55" width="100%" y="15">
							<rect
								height="100%"
								width="calc(100% - 112px)"
								x="37"
								fill={titlesBackgroundColor1}
							/>
							<svg width={`${93}%`}>
								<text
									x="55"
									y="42"
									fontSize="32px"
									fill={titlesFontColor1}
									fontWeight="bold"
									fontStyle="italic"
								>
									{' '}
									{text1}
								</text>
							</svg>

							<svg
								viewBox="0 0 7 10"
								height="100%"
								preserveAspectRatio="xMinYMid"
							>
								<path d="M6,10H0L6,0H7V10Z" fill={titlesBackgroundColor1} />
							</svg>

							<svg
								viewBox="0 0 14.42 10"
								height="100%"
								preserveAspectRatio="xMaxYMid"
							>
								<path
									d="M8.05,0l-6,9.91H5.5L11.5,0Z"
									fill={titlesBackgroundColor1}
								/>
								<path
									d="M12.77,0l-6,9.92L8.61,10,14.42.08Z"
									fill={titlesBackgroundColor1}
								/>
								<path d="M1,0H7L1,9.91H0V0Z" fill={titlesBackgroundColor1} />
							</svg>
						</svg>

						<svg height="20" x="-34.5px" y="73">
							<rect
								height="100%"
								width="calc(100% - 13px)"
								fill={titlesBackgroundColor2}
							/>
							<svg width={`${99}%`}>
								<text
									x="70"
									y="14"
									fontSize="15px"
									fontWeight="bold"
									fill={titlesFontColor2}
									fontStyle="italic"
								>
									{text2}
								</text>
							</svg>

							<svg
								viewBox="0 0 7 10"
								height="100%"
								preserveAspectRatio="xMaxYMid"
							>
								<path d="M1,0H7L1,9.91H0V0Z" fill={titlesBackgroundColor2} />
							</svg>
						</svg>
					</svg>
				);
			}

			case 7: {
				const { text1, text2 } = cardStrings7();

				return (
					<svg viewBox="0 0 1000 95" width="100%">
						<svg height="30" width="80%" x="20">
							<rect
								height="100%"
								width="calc(100% - 29px)"
								fill={titlesBackgroundColor3}
							/>
							<svg width={`${98}%`}>
								<text
									x="125"
									y="22"
									fontSize="22px"
									fill={titlesFontColor2}
									fontWeight="bold"
									fontStyle="italic"
								>
									{text1}
								</text>
							</svg>

							<svg
								viewBox="0 0 10 10"
								height="100%"
								preserveAspectRatio="xMaxYMid"
							>
								<path
									d="M10,10H0V0S10,0.5,10,10Z"
									fill={titlesBackgroundColor3}
								/>
							</svg>
						</svg>

						<svg height="60" width="95%" x="20" y="35">
							<rect
								height="100%"
								width="calc(100% - 59px)"
								fill={titlesBackgroundColor1}
							/>
							<svg width={`${97}%`}>
								<text
									x="125"
									y="44"
									fontSize="48px"
									fill={titlesFontColor1}
									fontWeight="bold"
									fontStyle="italic"
								>
									{text2}
								</text>
							</svg>

							<svg
								viewBox="0 0 10 10"
								height="100%"
								preserveAspectRatio="xMaxYMid"
							>
								<path
									d="M0,10V0h10S10,10,0,10Z"
									fill={titlesBackgroundColor1}
								/>
							</svg>
						</svg>

						<svg height="60" width="130" x="0" y="20">
							<rect
								height="100%"
								width="calc(100% - 29px)"
								fill={titlesBackgroundColor2}
							/>

							<svg
								viewBox="0 0 5 10"
								height="100%"
								preserveAspectRatio="xMaxYMid"
							>
								<path
									d="M0,0a5,5,0,0,1,5,5,5,5,0,0,1-5,5Z"
									fill={titlesBackgroundColor2}
								/>
							</svg>
						</svg>
					</svg>
				);
			}

			// somewhat of a template/simple version of title 2
			// case 42: {
			// 	return (<div className="4">
			// 		<svg viewBox="0 0 1000 100" width="100%">
			// 			<svg height="30" width={"width calculated with text"} x="200">

			// 				{/* width is 100% - width of end caps */}
			// 				<rect
			//					height="100%"
			//					width="calc(100% - 60px)"
			//					x="30"
			//					fill={titlesBackgroundColor1}
			//				/>
			// 				{/* font is placed at bottom left of rect ~4units
			// 				from bottom (height) and left (width of end cap) */}
			// 				<text x="32" y="25" fontSize="24px" className="top" fill={titlesFontColor1}>
			//					{name}
			//				</text>

			// 				{/* because height is 30, width of each is 30 (viewbox)*/}
			// 				<svg viewBox="0 0 10 10" height="100%" preserveAspectRatio="xMaxYMid">
			// 					<path d="M10,10H0V0S10,0.5,10,10Z" fill={titlesBackgroundColor2} />
			// 				</svg>

			// 				<svg viewBox="0 0 10 10" height="100%" preserveAspectRatio="xMinYMid">
			// 					<path d="M0,10h10V0S0,0.5,0,10Z" fill={titlesBackgroundColor2} />
			// 				</svg>
			// 			</svg>
			// 		</svg>
			// 	</div >);
			// }

			default: return (<></>);
		}
	}

	return (
		<div className={classNames('title-card', width, margin, position)}>
			{titleSvg()}
		</div >
	);
}
